/**
 * Rules Engine for Archetype Matching
 * Evaluates classifier tags against archetype conditions
 */

/**
 * Match a classifier result to the best archetype
 * @param {object} classifierTags - Tags from the classifier (abstraction_score, recursion_depth, etc.)
 * @param {array} archetypes - Array of archetype documents from Firestore
 * @returns {object} Best matching archetype with match score
 */
export function matchArchetype(classifierTags, archetypes) {
  if (!archetypes || archetypes.length === 0) {
    return {
      archetype: getDefaultArchetype(),
      score: 0,
      matched: false,
    };
  }

  // Filter only enabled archetypes
  const enabledArchetypes = archetypes.filter(a => a.enabled !== false);

  if (enabledArchetypes.length === 0) {
    return {
      archetype: getDefaultArchetype(),
      score: 0,
      matched: false,
    };
  }

  // Score each archetype
  const scoredArchetypes = enabledArchetypes.map(archetype => ({
    archetype,
    score: scoreArchetypeMatch(classifierTags, archetype.conditions || {}),
  }));

  // Sort by score (highest first)
  scoredArchetypes.sort((a, b) => b.score - a.score);

  // Return best match if score > 0, otherwise return default
  const bestMatch = scoredArchetypes[0];
  
  if (bestMatch.score > 0) {
    return {
      archetype: bestMatch.archetype,
      score: bestMatch.score,
      matched: true,
    };
  }

  // Find default archetype or return hardcoded default
  const defaultArchetype = enabledArchetypes.find(a => a.isDefault) || getDefaultArchetype();
  
  return {
    archetype: defaultArchetype,
    score: 0,
    matched: false,
  };
}

/**
 * Score how well classifier tags match archetype conditions
 * @param {object} tags - Classifier tags
 * @param {object} conditions - Archetype conditions
 * @returns {number} Match score (0-100)
 */
function scoreArchetypeMatch(tags, conditions) {
  let totalScore = 0;
  let conditionCount = 0;

  // Check each condition
  for (const [key, condition] of Object.entries(conditions)) {
    const tagValue = tags[key];
    
    if (tagValue === undefined || tagValue === null) {
      continue;
    }

    conditionCount++;

    // Handle different condition types
    if (typeof condition === 'object') {
      // Range condition (min/max)
      if (condition.min !== undefined || condition.max !== undefined) {
        const min = condition.min ?? -Infinity;
        const max = condition.max ?? Infinity;
        
        if (tagValue >= min && tagValue <= max) {
          // Calculate how well it fits within the range
          const range = max - min;
          const position = tagValue - min;
          const centerDistance = Math.abs(position - range / 2);
          const score = 100 * (1 - centerDistance / (range / 2));
          totalScore += Math.max(0, score);
        } else {
          // Outside range, penalize based on distance
          const distanceFromRange = tagValue < min ? (min - tagValue) : (tagValue - max);
          totalScore += Math.max(0, 50 - distanceFromRange * 50);
        }
      }
      
      // Array condition (one of these values)
      else if (Array.isArray(condition)) {
        if (condition.includes(tagValue)) {
          totalScore += 100;
        }
      }
      
      // Exact match object
      else if (condition.value !== undefined) {
        if (tagValue === condition.value) {
          totalScore += 100;
        }
      }
    } 
    // Simple exact match
    else {
      if (tagValue === condition) {
        totalScore += 100;
      }
    }
  }

  // Return average score across all conditions
  return conditionCount > 0 ? totalScore / conditionCount : 0;
}

/**
 * Get default archetype for fallback
 * @returns {object} Default archetype
 */
export function getDefaultArchetype() {
  return {
    id: 'default',
    name: 'Balanced Explorer',
    enabled: true,
    isDefault: true,
    conditions: {},
    reflectionQuestion: 'What pattern in your life would you most like to understand?',
    famousMinds: ['Leonardo da Vinci', 'Marie Curie'],
    careerVectors: {
      natural: 'Generalist',
      latent: 'Specialist in an emerging field',
    },
    customInstructions: `This person shows balanced cognitive patterns. Focus on:
- Identifying their strongest natural inclination
- Highlighting areas where they could specialize
- Encouraging depth in areas of genuine curiosity`,
  };
}

/**
 * Validate archetype conditions structure
 * @param {object} conditions - Conditions object to validate
 * @returns {object} { valid: boolean, errors: array }
 */
export function validateArchetypeConditions(conditions) {
  const errors = [];
  
  if (!conditions || typeof conditions !== 'object') {
    return { valid: false, errors: ['Conditions must be an object'] };
  }

  const validKeys = [
    'abstraction_score',
    'recursion_depth',
    'question_density',
    'cognitive_style',
    'emotional_tone',
    'temporal_focus',
    'complexity_level',
  ];

  for (const [key, condition] of Object.entries(conditions)) {
    if (!validKeys.includes(key)) {
      errors.push(`Unknown condition key: ${key}`);
      continue;
    }

    // Validate condition structure
    if (typeof condition === 'object' && !Array.isArray(condition)) {
      if (condition.min !== undefined && typeof condition.min !== 'number') {
        errors.push(`${key}.min must be a number`);
      }
      if (condition.max !== undefined && typeof condition.max !== 'number') {
        errors.push(`${key}.max must be a number`);
      }
      if (condition.min !== undefined && condition.max !== undefined && condition.min > condition.max) {
        errors.push(`${key}.min cannot be greater than max`);
      }
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Get seed data for initial archetypes
 * @returns {array} Array of archetype documents
 */
export function getArchetypeSeedData() {
  return [
    {
      id: 'architect-integrator',
      name: 'Architect-Integrator',
      enabled: true,
      isDefault: false,
      conditions: {
        abstraction_score: { min: 0.7, max: 1.0 },
        recursion_depth: { min: 0.6, max: 1.0 },
        complexity_level: ['high'],
      },
      reflectionQuestion: 'What am I optimizing that no longer matters?',
      famousMinds: ['Ray Dalio', 'Buckminster Fuller'],
      careerVectors: {
        natural: 'Systems Architect',
        latent: 'Futurist',
      },
      customInstructions: `This person thinks in systems and abstractions. They:
- See patterns and connections others miss
- Often intellectualize to avoid discomfort
- Build frameworks but may struggle with execution
- Optimize for long-term over short-term

In the report:
- Emphasize their ability to see the big picture
- Challenge them to act on insights
- Suggest one concrete experiment to test their frameworks`,
    },
    {
      id: 'grounded-realist',
      name: 'Grounded Realist',
      enabled: true,
      isDefault: false,
      conditions: {
        abstraction_score: { min: 0.0, max: 0.4 },
        question_density: { min: 0.6, max: 1.0 },
        cognitive_style: ['concrete', 'reactive'],
      },
      reflectionQuestion: 'What skill could I learn in a week that makes my work easier?',
      famousMinds: ['Ernest Hemingway', 'Barbara Corcoran'],
      careerVectors: {
        natural: 'Operator',
        latent: 'Strategic Advisor',
      },
      customInstructions: `This person is pragmatic and action-oriented. They:
- Value practical results over theory
- Ask questions and test assumptions
- Act quickly with available information
- May miss long-term strategic opportunities

In the report:
- Validate their bias toward action
- Highlight one pattern they're missing by moving too fast
- Suggest a weekly reflection practice`,
    },
    {
      id: 'reflective-synthesizer',
      name: 'Reflective Synthesizer',
      enabled: true,
      isDefault: false,
      conditions: {
        abstraction_score: { min: 0.4, max: 0.7 },
        recursion_depth: { min: 0.5, max: 0.9 },
        emotional_tone: ['balanced', 'optimistic'],
      },
      reflectionQuestion: 'What would I do if I trusted my judgment more?',
      famousMinds: ['Carl Sagan', 'Brené Brown'],
      careerVectors: {
        natural: 'Integrator',
        latent: 'Teacher or Guide',
      },
      customInstructions: `This person balances thinking and feeling. They:
- Synthesize multiple perspectives
- Reflect deeply before acting
- May overthink decisions
- Value meaning and connection

In the report:
- Emphasize their ability to hold complexity
- Challenge their tendency to delay decisions
- Suggest trusting their first instinct on small decisions`,
    },
    {
      id: 'reactive-pragmatist',
      name: 'Reactive Pragmatist',
      enabled: true,
      isDefault: false,
      conditions: {
        recursion_depth: { min: 0.0, max: 0.3 },
        temporal_focus: ['present'],
        cognitive_style: ['reactive'],
      },
      reflectionQuestion: 'What am I avoiding by staying busy?',
      famousMinds: ['Anthony Bourdain', 'Richard Branson'],
      careerVectors: {
        natural: 'Executor',
        latent: 'Crisis Manager',
      },
      customInstructions: `This person lives in the present and acts on instinct. They:
- Respond quickly to immediate needs
- Thrive in dynamic environments
- May lack long-term planning
- Value experience over analysis

In the report:
- Validate their responsiveness and adaptability
- Highlight the cost of constant reactivity
- Suggest one weekly planning ritual`,
    },
    {
      id: 'default',
      name: 'Balanced Explorer',
      enabled: true,
      isDefault: true,
      conditions: {},
      reflectionQuestion: 'What pattern in my life would I most like to understand?',
      famousMinds: ['Leonardo da Vinci', 'Marie Curie'],
      careerVectors: {
        natural: 'Generalist',
        latent: 'Specialist in an emerging field',
      },
      customInstructions: `This person shows balanced cognitive patterns. Focus on:
- Identifying their strongest natural inclination
- Highlighting areas where they could specialize
- Encouraging depth in areas of genuine curiosity`,
    },
  ];
}

